import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  ExternalLink,
  Search,
  Clock,
  Database,
  Link as LinkIcon,
  Activity
} from "lucide-react";

export default function ExploreBlockchain() {
  const navigate = useNavigate(); // ✅ for navigation
  return (
    <section id="explore" className="py-20 bg-gradient-to-br from-slate-900 via-primary to-slate-800 text-white relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        {/* Animated nodes */}
        <div className="absolute top-20 left-10 w-4 h-4 bg-accent rounded-full opacity-60 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-3 h-3 bg-white rounded-full opacity-40 animate-pulse delay-500"></div>
        <div className="absolute bottom-32 left-1/4 w-2 h-2 bg-accent rounded-full opacity-50 animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 right-1/3 w-5 h-5 bg-white rounded-full opacity-30 animate-pulse delay-1500"></div>

        {/* Connecting lines */}
        <svg className="absolute inset-0 w-full h-full opacity-20">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#ff9933" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          <line x1="10%" y1="20%" x2="90%" y2="30%" stroke="url(#lineGradient)" strokeWidth="1" />
          <line x1="20%" y1="60%" x2="80%" y2="80%" stroke="url(#lineGradient)" strokeWidth="1" />
          <line x1="15%" y1="80%" x2="75%" y2="40%" stroke="url(#lineGradient)" strokeWidth="1" />
        </svg>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Explore the Blockchain
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
Search and verify projects by ward, location, or project ID, track them through map-based details, and access complete history including cost, materials, and past records.

          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Explorer Features */}
          <div className="space-y-6">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <Search className="w-8 h-8 text-accent" />
                  <h3 className="text-xl font-semibold">Search & Verify</h3>
                </div>
                <p className="text-blue-100 leading-relaxed">
                  Search for any document using certificate number, property ID, or transaction hash.
                  Instantly verify authenticity and view complete ownership history.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <Clock className="w-8 h-8 text-accent" />
                  <h3 className="text-xl font-semibold">Real-Time Tracking</h3>
                </div>
                <p className="text-blue-100 leading-relaxed">
                  Monitor live transactions as they happen. See new registrations,
                  transfers, and modifications in real-time with complete transparency.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <Database className="w-8 h-8 text-accent" />
                  <h3 className="text-xl font-semibold">Complete History</h3>
                </div>
                <p className="text-blue-100 leading-relaxed">
                  Access complete transaction history for any document.
                  View all past owners, transfers, and modifications with timestamps.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Mock Explorer Interface */}
          <div className="relative">
            <Card className="bg-white/95 backdrop-blur-sm text-foreground shadow-2xl">
              <CardContent className="p-6">
                <div className="flex items-center space-x-2 mb-6 pb-4 border-b">
                  <LinkIcon className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">
                    Blockchain Explorer
                  </span>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search by Certificate ID, Hash, or Property..."
                      className="w-full pl-10 pr-4 py-3 border rounded-lg bg-secondary/50 text-sm"
                      disabled
                    />
                  </div>
                </div>

                {/* Recent Transactions */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground mb-3 flex items-center">
                    <Activity className="w-4 h-4 mr-2 text-green-600" />
                    Recent Transactions
                  </h4>


                  {/* {[ { id: "TDR-2024-001", type: "Registration", time: "2 min ago", status: "Confirmed" }, { id: "TDR-2024-002", type: "Transfer", time: "5 min ago", status: "Confirmed" }, { id: "TDR-2024-003", type: "Verification", time: "8 min ago", status: "Pending" } ] */}


                  {[
                    { id: "—", type: "No transactions yet", time: "Be the first to add a block!", status: "—" },
                    { id: "—", type: "No transactions yet", time: "Be the first to add a block!", status: "—" },
                    { id: "—", type: "No transactions yet", time: "Be the first to add a block!", status: "—" }
                  ].map((tx, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-foreground">{tx.id}</p>
                        <p className="text-xs text-muted-foreground">{tx.type} • {tx.time}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${tx.status === 'Confirmed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {tx.status}
                      </span>
                    </div>
                  ))}
                </div>




                {/* Network Stats */}
                <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">0</p>
                    <p className="text-xs text-muted-foreground">Total Documents</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-accent">100%</p>
                    <p className="text-xs text-muted-foreground">Uptime</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <Button
            size="lg"
            className="bg-accent hover:bg-accent/90 text-accent-foreground"
            onClick={() => navigate("/blockchain")}
          >
            Go to Blockchain Explorer
            <ExternalLink className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-blue-100 mt-4 text-sm">
            Access the full blockchain explorer with advanced search and analytics
          </p>
        </div>
      </div>
    </section>
  );
}